import type { ComponentProps } from "react";
import { useEffect, useMemo } from "react";
import type { NativeBridgeType } from "../pages/Login/hooks/index.ts";
import { useNativeBridge } from "../pages/Login/hooks/index.ts";
import { hasExoticProtocol, hasJavascriptProtocol } from "../util/index.ts";

import type { BamHooks } from "../vendor/bam-hooks.ts";

type UTMParams = {
  /** @default bingo */
  source?: string;
  /** E.g., web, app, etc. */
  medium?: string;
  /** @default bingo */
  campaign?: string;
  /** E.g., 'live-feed', 'featured-message', 'menu-link', etc. */
  content?: string;
};

/**
 * Given an href, this returns appropriate link props for an anchor tag,
 * depending on:
 *
 * 1. whether the link is internal or external and
 * 2. what the bridge type is
 *
 * If the url is exotic, and the user is in a web interface, then the function
 * returns null.
 *
 * It's safe to use this for any href that is potentially external. If it's
 * actually internal, it returns `{ href: href }`.
 */
export function useLinkProps(
  href: string | null | undefined,
  options: {
    utm?: UTMParams;
  } = {
    utm: {},
  },
): ComponentProps<"a"> | null {
  const bridge = useNativeBridge();
  const strategy = STRATEGIES[bridge];

  const utm = useMemo(
    () => ({
      source: options.utm?.source,
      medium: options.utm?.medium,
      campaign: options.utm?.campaign,
      content: options.utm?.content,
    }),
    [
      options.utm?.source,
      options.utm?.medium,
      options.utm?.campaign,
      options.utm?.content,
    ],
  );

  useEffect(() => {
    if (bridge === "MLBAM_BALLPARK_NATIVE") {
      import("../vendor/bam-hooks").catch((error) => {
        console.error("Unable to load bamhooks", error);
      });
    }
  }, [bridge]);

  return useMemo(() => {
    const url = href ? toURLSafe(href) : undefined;

    if (!url) {
      return { href: undefined };
    }

    if (!strategy.shouldRender(url)) {
      return null;
    }
    if (strategy.isExternal(url)) {
      addUTMParams(url, utm || {});

      return strategy.getExternalProps(url);
    }
    return { href: url.toString() };
  }, [strategy, href, utm]);
}

function addUTMParams(url: URL, utm: UTMParams) {
  const searchParams = new URLSearchParams(url.search);

  if (!searchParams.has("utm_source")) {
    searchParams.set("utm_source", utm.source ?? "bingo");
  }

  if (!searchParams.has("utm_medium") && utm.medium) {
    searchParams.set("utm_medium", utm.medium);
  }

  if (!searchParams.has("utm_campaign")) {
    searchParams.set("utm_campaign", utm.campaign ?? "bingo");
  }

  if (!searchParams.has("utm_content") && utm.content) {
    searchParams.set("utm_content", utm.content);
  }

  url.search = searchParams.toString();
}

type LaunchExternalStrategy = {
  shouldRender: (url: URL) => boolean;
  isExternal: (url: URL) => boolean;
  getExternalProps(url: URL): ComponentProps<"a"> | null;
};

const MLBAM_BALLPARK_INTERNAL_DOMAINS = ["mlb.com", "mlbshop.com"];

const STRATEGIES: Record<NativeBridgeType, LaunchExternalStrategy> = {
  DEFAULT_WEB: {
    shouldRender(url) {
      // Don't render links with javascript: protocol for security reasons
      return !hasJavascriptProtocol(url);
    },
    isExternal(url) {
      return (isHTTP(url) && isOtherDomain(url)) || hasExoticProtocol(url);
    },
    getExternalProps(url) {
      return {
        href: url.toString(),
        target: "_blank",
        rel: "noopener",
      };
    },
  },
  MLBAM_BALLPARK_NATIVE: {
    shouldRender() {
      return true;
    },
    isExternal(url) {
      // mlb.com and mlbshop.com URLs should launch internally for MLBAM
      return (
        isHTTP(url) &&
        isOtherDomain(url) &&
        !MLBAM_BALLPARK_INTERNAL_DOMAINS.some((domain) =>
          url.host.includes(domain),
        )
      );
    },
    getExternalProps(url) {
      return {
        href: url.toString(),
        target: "_blank",
        rel: "noopener",
        onClick(e) {
          const bamhooks = (window as { BamHooks?: BamHooks }).BamHooks;

          if (!bamhooks) {
            console.warn(
              "Bridge type is MLBAM_BALLPARK_NATIVE, but bamhooks is undefined",
            );
            return;
          }

          try {
            bamhooks.launchExternal(url.toString());
            e.preventDefault();
          } catch (_error) {
            console.error("Failed to launch externally");
          }
        },
      };
    },
  },
};

const toURLSafe = (input: string) => {
  try {
    return new URL(input);
  } catch (error) {
    console.error("Failed to convert to URL:", input, error);
    return undefined;
  }
};

const isHTTP = (url: URL) =>
  url.protocol === "http:" || url.protocol === "https:";

const isOtherDomain = (url: URL) => url.host !== window.location.host;
